import { Injectable, inject } from '@angular/core';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpService } from 'src/app/service/http/http.service';
import { DashboardData } from '../components/dashboard-stats/dashboard-stats.component';
import { TemporalDataItem, RuptureModel, RuptureModelsJson } from '../components/advanced-analysis/advanced-analysis.component';
import { HttpParams } from '@angular/common/http';
import { SeriesDetail } from '../models/lakehouse.model';

export type ItemConnected = SeriesDetail;

export interface Version {
  id: string;
  version: string;
  date: string;
}

export interface TabData {
  dashboardData: DashboardData | null;
  advancedAnalysisTemporalData: TemporalDataItem[] | null;
  advancedAnalysisRupturesData: Record<string, RuptureModel> | null;
}

export interface Job {
  _id: string;
  version: number;
  status: string;
  serie_id: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  storage_path: string;
  ecs_resource: string;
  automated?: boolean;
}

export interface Layer {
  _id: string;
  job_id: string;
  layer: string;
  __v: number;
  createdAt: string;
  status: string;
  updatedAt: string;
  steps: StepInterface;
  message?: string;
}

export interface StepInterface {
  total: number;
  progress: number;
}

interface DefaultResponse {
  count: number;
  rows: [];
}

export interface LayerLogsResponse {
  layer_id: string;
  time: string;
  status: string;
  type: string;
  output: string;
}

export interface SeriesBatchExecutionPayload {
  serie_ids: string[];
  start_date?: string;
  end_date?: string;
  trigger_etl_dag?: boolean;
  file?: File;
}

/** Status filter sent to GET /jobs. `in_progress` is expanded by the API. */
export type JobsStatusFilter = 'completed' | 'failure' | 'in_progress';

export interface GetItemJobsFilters {
  status?: JobsStatusFilter;
  /** When true, only automated executions. */
  automated?: boolean;
}

export interface JobStatusCounts {
  all: number;
  completed: number;
  failure: number;
  in_progress: number;
}

@Injectable({
  providedIn: 'root'
})
export class DetailConnectedItemService {
  private readonly httpService = inject(HttpService);
  private readonly layerLogsUpdated$ = new Subject<LayerLogsResponse>();

  getLayerLogsUpdates(): Observable<LayerLogsResponse> {
    return this.layerLogsUpdated$.asObservable();
  }

  emitLayerUpdatedEvent(log: LayerLogsResponse): void {
    this.layerLogsUpdated$.next(log);
  }

  private readonly layerUpdated$ = new Subject<Layer>();

  getLayerUpdates(): Observable<Layer> {
    return this.layerUpdated$.asObservable();
  }

  emitLayerUpdate(layer: Layer): void {
    this.layerUpdated$.next(layer);
  }

  /**
   * Busca informações gerais do item conectado
   */
  getItemInformation(itemId: string): Observable<ItemConnected> {
    return this.httpService.genericGet<ItemConnected>('series', itemId);
  }

  /**
   * Busca as versões/execuções do item conectado
   */
  getItemJobs(
    itemId: string,
    page = 1,
    limit = 15,
    filters: GetItemJobsFilters = {}
  ): Observable<DefaultResponse> {
    const skip = Math.max(0, (page - 1) * limit);
    let params = new HttpParams()
      .set('serie_id', itemId)
      .set('page', page.toString())
      .set('skip', skip.toString())
      .set('limit', limit.toString());

    if (filters.status) {
      params = params.set('status', filters.status);
    }
    if (filters.automated === true) {
      params = params.set('automated', 'true');
    }

    return this.httpService.genericGet<DefaultResponse>('jobs', undefined, params);
  }

  /**
   * Contagens por status do histórico, respeitando o filtro de tipo (automated).
   */
  getItemJobsStatusCounts(
    itemId: string,
    automated?: boolean
  ): Observable<JobStatusCounts> {
    let params = new HttpParams().set('serie_id', itemId);
    if (automated === true) {
      params = params.set('automated', 'true');
    }
    return this.httpService.genericGet<JobStatusCounts>('jobs/status-counts', undefined, params);
  }

  /**
   * Busca as camadas do item conectado
   */
  getItemLayersByJobId(jobId: string): Observable<DefaultResponse> {
    return this.httpService.genericGet<DefaultResponse>('layers', undefined, new HttpParams().set('job_id', jobId));
  }

  /**
   * Busca dados do dashboard (informações gerais)
   */
  getDashboardData(layerId: string, jobId: string): Observable<DefaultResponse> {
    return this.httpService.genericGet<DefaultResponse>(
      'series-summary-output',
      undefined,
      new HttpParams().set('layer_id', layerId).set('job_id', jobId)
    );
  }

  /**
   * Busca dados de análise avançada (temporal)
   */
  getAdvancedAnalysisTemporalData(layerId: string, jobId: string): Observable<DefaultResponse> {
    return this.httpService.genericGet<DefaultResponse>(
      'time-series-columns-output',
      null,
      new HttpParams().set('layer_id', layerId).set('job_id', jobId)
    );
  }

  /**
   * Busca dados de rupturas para análise avançada
   */
  getAdvancedAnalysisRupturesData(layerId: string, jobId: string): Observable<RuptureModelsJson> {
    return this.httpService.genericGet<RuptureModelsJson>(
      'ruptures-output',
      null,
      new HttpParams().set('layer_id', layerId).set('job_id', jobId)
    );
  }

  /**
   * Inicia execução em lote de séries com intervalo de datas.
   * POST /jobs/serie/batch
   */
  startSeriesBatch(payload: SeriesBatchExecutionPayload): Observable<Job> {
    if (payload.file) {
      const formData = new FormData();
      formData.append('serie_ids', JSON.stringify(payload.serie_ids));
      if (payload.start_date) formData.append('start_date', payload.start_date);
      if (payload.end_date) formData.append('end_date', payload.end_date);
      if (payload.trigger_etl_dag !== undefined) {
        formData.append('trigger_etl_dag', String(payload.trigger_etl_dag));
      }
      formData.append('file', payload.file, payload.file.name);
      return this.httpService.genericPost<Job>('jobs/serie/batch', formData, '');
    }

    const { file: _file, ...body } = payload;
    return this.httpService.genericPostNotData<Job>('jobs/serie/batch', body);
  }

  /**
   * Busca todos os dados das tabs de uma vez
   */
  getAllTabData(layerId: string, jobId: string): Observable<TabData> {
    const requests: Record<string, Observable<any>> = {
      dashboardData: this.getDashboardData(layerId, jobId).pipe(
        map((data: DefaultResponse) => data.rows as DashboardData[]),
        catchError(() => of(null))
      ),
      advancedAnalysisTemporalData: this.getAdvancedAnalysisTemporalData(layerId, jobId).pipe(
        map((data: DefaultResponse) => data.rows as TemporalDataItem[]),
        catchError(() => of(null))
      ),
      // Ruptures desabilitado por enquanto (evita GET /ruptures-output)
      advancedAnalysisRupturesData: of(null),
    };

    return forkJoin(requests).pipe(
      map((results) => ({
        dashboardData: results.dashboardData,
        advancedAnalysisTemporalData: results.advancedAnalysisTemporalData,
        advancedAnalysisRupturesData: results.advancedAnalysisRupturesData,
      }))
    );
  }

  getLayerLogs(layerId: string): Observable<LayerLogsResponse[]> {
    return this.httpService.genericGet<LayerLogsResponse[]>('layer-logs', undefined, new HttpParams().set('layer_id', layerId));
  }
}

